import { UserPermissions, CategoryAccess } from './api'

export type ProtocolPermissions = {
  isAdmin: boolean
  isCategoryManager: boolean
  isCategoryManagerOf: (categoryId: number) => boolean
  isApprovingManager: (categoryId: number) => boolean
  isViewer: (categoryId: number) => boolean
  allAvailableCategories: number[]
  // categories
  canCreateCategory: boolean
  canDeleteCategory: boolean
  canEditCategoryById: (categoryId: number) => boolean
  // protocols
  canCreateProtocol: boolean
  canDeleteProtocol: boolean
  canArchiveProtocol: boolean
  canShareProtocol: (categoryId: number) => boolean
  canViewStatusHistory: (categoryId: number) => boolean
  canViewApprovers: (categoryId: number) => boolean
  canPublish: (categoryId: number) => boolean
  isMainManager: boolean
  // archive
  canViewArchive: boolean
  canDeleteArchivedProtocol: boolean
  canRestoreProtocol: boolean
  // main manager
  canManageMainManagers: boolean
}

export function getProtocolPermissions(userPermissions: UserPermissions | null): ProtocolPermissions {
  const isAdmin = !!(userPermissions?.isAdmin || userPermissions?.isMainManager)

  const categoryAccess: CategoryAccess[] = Array.isArray(userPermissions?.allAvailableCategories)
    ? userPermissions!.allAvailableCategories!
    : []

  // A user can hold several unique roles in the same category at once (e.g. Category
  // Manager + Approver) - check per role with .some(), never take "the" role with .find(),
  // which would silently hide every role but the first one found.
  const hasRoleInCategory = (categoryId: number, roleId: number): boolean =>
    categoryAccess.some(c => c.categoryId === categoryId && c.memberRoleId === roleId)

  const isCategoryManager = !isAdmin && categoryAccess.some(c => c.memberRoleId === 1)
  const isCategoryManagerOf = (categoryId: number) => hasRoleInCategory(categoryId, 1)
  const isApprovingManager = (categoryId: number) => hasRoleInCategory(categoryId, 2)
  const isViewer = (categoryId: number) => !isAdmin && hasRoleInCategory(categoryId, 3)

  const allAvailableCategoryIds = categoryAccess.map(c => c.categoryId)

  return {
    isAdmin,
    isCategoryManager,
    isCategoryManagerOf,
    isApprovingManager,
    isViewer,
    allAvailableCategories: allAvailableCategoryIds,

    canCreateCategory: isAdmin,
    canDeleteCategory: isAdmin,
    canEditCategoryById: (categoryId: number) => isAdmin || isCategoryManagerOf(categoryId),

    // Category managers, not approvers: an approver's part starts once a protocol
    // is out for approval.
    canCreateProtocol: isAdmin || categoryAccess.some(c => c.memberRoleId === 1),
    canDeleteProtocol: isAdmin,
    canArchiveProtocol: isAdmin || isCategoryManager,
    canShareProtocol: (categoryId: number) => isAdmin || isCategoryManager || isApprovingManager(categoryId),
    canViewStatusHistory: (categoryId: number) => isAdmin || isCategoryManager || isApprovingManager(categoryId),
    // Admin / Main Manager / Category Manager only - not approving manager or viewer.
    canViewApprovers: (categoryId: number) => isAdmin || isCategoryManager || isCategoryManagerOf(categoryId),
    // Category Manager of this category only - not Admin, not Main Manager.
    canPublish: (categoryId: number) => isCategoryManagerOf(categoryId),

    canViewArchive: true,
    canDeleteArchivedProtocol: isAdmin,
    canRestoreProtocol: isAdmin || isCategoryManager,
    isMainManager: userPermissions?.isMainManager,
    canManageMainManagers: isAdmin,
  }
}
